import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MDRBadge } from "@/components/MDRBadge";
import { toast } from "sonner";
import {
  serviceExecutionTimesData, operatorsData, getPharmacyById, serviceCatalogData
} from "@/data/platformData";
import { servicesData } from "@/data/mockData";
import {
  Settings, Save, ArrowLeft, Clock, Users, Euro
} from "lucide-react";

const PHARMACY_ID = "ph1";

export const ManagerConfigurationPage = () => {
  const navigate = useNavigate();
  const pharmacy = getPharmacyById(PHARMACY_ID);

  // Service exec times merged with full service list
  const initialTimes = servicesData.map(s => {
    const found = serviceExecutionTimesData.find(t => t.pharmacyId === PHARMACY_ID && t.serviceId === s.id);
    return {
      serviceId: s.id,
      name: s.name,
      duration: found?.duration ?? s.duration,
      bufferBefore: found?.bufferBefore ?? 0,
      bufferAfter: found?.bufferAfter ?? 0,
      enabled: true
    };
  });
  const [times, setTimes] = useState(initialTimes);

  const initialPrices = serviceCatalogData.map(s => ({ serviceId: s.id, name: s.name, price: s.price }));
  const [prices, setPrices] = useState(initialPrices);

  const initialOps = operatorsData.map(op => ({
    ...op,
    associated: op.pharmacyIds.includes(PHARMACY_ID)
  }));
  const [ops, setOps] = useState(initialOps);

  const updateTime = (serviceId, patch) => {
    setTimes(prev => prev.map(t => t.serviceId === serviceId ? { ...t, ...patch } : t));
  };
  const updatePrice = (serviceId, value) => {
    setPrices(prev => prev.map(p => p.serviceId === serviceId ? { ...p, price: value } : p));
  };
  const toggleOp = (id) => {
    setOps(prev => prev.map(o => o.id === id ? { ...o, associated: !o.associated } : o));
  };

  const saveAll = () => {
    toast.success("Configurazione salvata", { description: `${times.filter(t => t.enabled).length} prestazioni attive · ${ops.filter(o => o.associated).length} operatori associati` });
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/manager")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Panoramica
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Settings className="h-4 w-4 text-indigo-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Configurazione</span>
              <MDRBadge variant="admin" size="sm" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Tempi, tariffario e operatori</h1>
            <p className="text-sm text-muted-foreground mt-1">{pharmacy?.name}</p>
          </div>
          <Button variant="premium" onClick={saveAll} className="gap-2" data-testid="config-save-btn">
            <Save className="h-4 w-4" /> Salva configurazione
          </Button>
        </div>
      </div>

      <Tabs defaultValue="times" className="space-y-4">
        <TabsList>
          <TabsTrigger value="times" data-testid="config-tab-times">Tempi</TabsTrigger>
          <TabsTrigger value="prices" data-testid="config-tab-prices">Tariffario</TabsTrigger>
          <TabsTrigger value="operators" data-testid="config-tab-operators">Operatori</TabsTrigger>
        </TabsList>

        <TabsContent value="times" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-indigo-600" /> Tempi di esecuzione
              </CardTitle>
              <p className="text-xs text-muted-foreground">Durata e buffer pre/post per la pianificazione automatica degli slot</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {times.map(t => (
                <div key={t.serviceId} className="grid grid-cols-12 gap-3 items-center p-3 rounded-lg border bg-card">
                  <div className="col-span-12 md:col-span-4 flex items-center gap-2">
                    <Switch checked={t.enabled} onCheckedChange={() => updateTime(t.serviceId, { enabled: !t.enabled })} />
                    <span className={`text-sm font-medium ${!t.enabled ? "text-muted-foreground line-through" : ""}`}>{t.name}</span>
                  </div>
                  <div className="col-span-4 md:col-span-2">
                    <Label className="text-[10px] text-muted-foreground">Durata (min)</Label>
                    <Input type="number" min="1" value={t.duration} onChange={(e) => updateTime(t.serviceId, { duration: Number(e.target.value) })} className="h-8 text-sm" data-testid={`config-time-duration-${t.serviceId}`} />
                  </div>
                  <div className="col-span-4 md:col-span-3">
                    <Label className="text-[10px] text-muted-foreground">Buffer prima (min)</Label>
                    <Input type="number" min="0" value={t.bufferBefore} onChange={(e) => updateTime(t.serviceId, { bufferBefore: Number(e.target.value) })} className="h-8 text-sm" />
                  </div>
                  <div className="col-span-4 md:col-span-3">
                    <Label className="text-[10px] text-muted-foreground">Buffer dopo (min)</Label>
                    <Input type="number" min="0" value={t.bufferAfter} onChange={(e) => updateTime(t.serviceId, { bufferAfter: Number(e.target.value) })} className="h-8 text-sm" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="prices" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Euro className="h-4 w-4 text-emerald-600" /> Tariffario prestazioni
              </CardTitle>
              <p className="text-xs text-muted-foreground">Prezzi base esposti in fase di prenotazione (pre-sconto campagne)</p>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 md:grid-cols-2">
                {prices.map(p => (
                  <div key={p.serviceId} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card">
                    <span className="text-sm font-medium truncate">{p.name}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Euro className="h-3 w-3 text-muted-foreground" />
                      <Input type="number" min="0" step="0.5" value={p.price} onChange={(e) => updatePrice(p.serviceId, Number(e.target.value))} className="w-24 h-8 text-sm" data-testid={`config-price-${p.serviceId}`} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="operators" className="space-y-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-sky-600" /> Operatori associati
              </CardTitle>
              <p className="text-xs text-muted-foreground">Abilita gli operatori autorizzati a operare in questa farmacia</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {ops.map(op => (
                <div key={op.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div>
                    <p className="font-medium text-sm">{op.name}</p>
                    <p className="text-xs text-muted-foreground">{op.email} · {op.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {op.associated && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Attivo</Badge>}
                    <Switch checked={op.associated} onCheckedChange={() => toggleOp(op.id)} data-testid={`config-op-toggle-${op.id}`} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManagerConfigurationPage;
